import { router } from "@inertiajs/react";
import { useState } from "react";
import CoverageAccordionHeader from "@/components/domains/CoverageAccordionHeader";
import CoverageAuditDialog, { type CoverageAuditDialogStep } from "@/components/domains/CoverageAuditDialog";
import CoverageAuditQuotaDialog from "@/components/domains/CoverageAuditQuotaDialog";
import DomainTestUserSection from "@/components/domains/DomainTestUserSection";
import JourneyRow from "@/components/domains/JourneyRow";
import JourneysMappingComplete from "@/components/domains/JourneysMappingComplete";
import JourneysMappingFailed from "@/components/domains/JourneysMappingFailed";
import JourneysMappingProgress from "@/components/domains/JourneysMappingProgress";
import type { DomainJourneys, DomainTestUser, JourneyRow as JourneyRowData, SubscriptionTier } from "@/types";
import { domainShowPartialVisit, inertiaVisitFlash } from "@/utils/domainShowReload";

type Props = {
  domainId: string;
  journeys: DomainJourneys;
  plan: SubscriptionTier;
  auditsUsed: number;
  auditsRemaining: number;
  auditLimit: number;
  auditsRefreshAt: string | null;
  canStartAudits?: boolean;
  testUser?: DomainTestUser;
  expanded: boolean;
  onToggle?: () => void;
  onWatchMapping?: (interactionId: string) => void;
  onWatchJourney?: (journey: JourneyRowData) => void;
};

type AuditDialogState = {
  journey: JourneyRowData;
  step: CoverageAuditDialogStep;
  alreadyInFlight?: boolean;
};

export default function JourneysSection({
  domainId,
  journeys,
  plan,
  auditsUsed,
  auditsRemaining,
  auditLimit,
  auditsRefreshAt,
  canStartAudits = true,
  testUser,
  expanded,
  onToggle,
  onWatchMapping,
  onWatchJourney,
}: Props) {
  const [auditingJourneyId, setAuditingJourneyId] = useState<string | null>(null);
  const [auditDialog, setAuditDialog] = useState<AuditDialogState | null>(null);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);

  const subtitle = subtitleFor(journeys, testUser);
  const showTestUser = Boolean(testUser?.can_configure);
  const awaitingTestUser = Boolean(showTestUser && !testUser?.configured);

  const closeAuditDialog = () => {
    if (auditDialog?.step === "starting") {
      return;
    }

    setAuditDialog(null);
  };

  const startAudit = (journey: JourneyRowData) => {
    if (auditingJourneyId) {
      return;
    }

    setAuditingJourneyId(journey.id);
    setAuditDialog({ journey, step: "starting" });
    router.post(
      `/domains/${domainId}/scenarios/${journey.id}/audits`,
      {},
      {
        ...domainShowPartialVisit,
        onSuccess: (inertiaPage) => {
          const flash = inertiaVisitFlash(inertiaPage);
          if (flash.error) {
            setAuditDialog(null);
            return;
          }

          setAuditDialog({
            journey,
            step: "started",
            alreadyInFlight: Boolean(flash.notice),
          });
        },
        onError: () => setAuditDialog(null),
        onFinish: () => setAuditingJourneyId(null),
      },
    );
  };

  const confirmAudit = () => {
    const journey = auditDialog?.journey;
    if (!journey || auditDialog.step !== "confirm" || auditingJourneyId) {
      return;
    }

    startAudit(journey);
  };

  const requestAudit = (journey: JourneyRowData) => {
    if (auditingJourneyId) {
      return;
    }

    if (auditsRemaining <= 0) {
      setQuotaDialogOpen(true);
      return;
    }

    setAuditDialog({ journey, step: "confirm" });
  };

  return (
    <section id="journeys" className="dashboard-section scroll-mt-5">
      <div className="dashboard-section-body">
        <CoverageAccordionHeader title="Journeys" subtitle={subtitle} expanded={expanded} onToggle={onToggle} />

        {expanded ? (
          <>
            {showTestUser && testUser ? <DomainTestUserSection domainId={domainId} testUser={testUser} /> : null}
            {journeys.status === "mapping" ? (
              <JourneysMappingProgress
                mappingInteractionId={journeys.mapping_interaction_id}
                onWatchMapping={onWatchMapping}
              />
            ) : null}
            {journeys.status === "failed" ? (
              <JourneysMappingFailed domainId={domainId} journeys={journeys} onWatchMapping={onWatchMapping} />
            ) : null}
            {journeys.status === "mapped" ? (
              <JourneysMappingComplete domainId={domainId} hostname={journeys.hostname} rows={journeys.rows} />
            ) : null}
            {journeys.status === "ready" && journeys.rows.length === 0 && !awaitingTestUser ? (
              <p className="mt-6 rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                No signed-in journeys yet.
              </p>
            ) : null}
            {journeys.status === "ready" && journeys.rows.length > 0 ? (
              <div className="mt-6 divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
                {journeys.rows.map((journey) => (
                  <JourneyRow
                    key={journey.id}
                    journey={journey}
                    auditing={auditingJourneyId === journey.id}
                    canStartAudits={canStartAudits}
                    auditBusy={auditingJourneyId !== null}
                    onAudit={requestAudit}
                    onWatch={onWatchJourney}
                  />
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {canStartAudits ? (
        <>
          <CoverageAuditDialog
            open={auditDialog !== null}
            step={auditDialog?.step ?? "confirm"}
            kind="journey"
            label={auditDialog?.journey.title ?? null}
            alreadyInFlight={Boolean(auditDialog?.alreadyInFlight)}
            auditsUsed={auditsUsed}
            auditLimit={auditLimit}
            auditsRefreshAt={auditsRefreshAt}
            onClose={closeAuditDialog}
            onConfirm={confirmAudit}
            onWatchLive={() => {
              if (!auditDialog) {
                return;
              }

              onWatchJourney?.(auditDialog.journey);
              setAuditDialog(null);
            }}
          />

          <CoverageAuditQuotaDialog
            open={quotaDialogOpen}
            variant={plan === "free" ? "upgrade" : "topup"}
            auditLimit={auditLimit}
            auditsRefreshAt={auditsRefreshAt}
            onDismiss={() => setQuotaDialogOpen(false)}
          />
        </>
      ) : null}
    </section>
  );
}

function subtitleFor(journeys: DomainJourneys, testUser?: DomainTestUser) {
  switch (journeys.status) {
    case "upgrade":
      return "What your users do after they log in";
    case "mapping":
      return "Mapping signed-in jobs";
    case "failed":
      return "Mapping did not find journeys to list";
    case "mapped":
      return "Mapping complete";
    default:
      if (testUser?.can_configure && !testUser.configured) {
        return "Add a test user to audit signed-in jobs";
      }
      return "Signed-in jobs we can audit";
  }
}
